import { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../utils/api';
import { GRANTABLE_TOOL_KEYS, tools, visibleToolKeys } from '../../tools/catalog';
import { useToolAccess } from '../../tools/ToolAccessContext';
import InviteMemberModal from './InviteMemberModal';
import ApproveAccessModal from './ApproveAccessModal';
import ProjectAccessSelect from './ProjectAccessSelect';

const ACCESS_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'View', label: 'View' },
    { value: 'Edit', label: 'Edit' }
];

function formatInviteSentDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export default function OrganizationDashboard({ tab = 'members' }) {
    const { isRootAdmin, isSuperAdmin, canEditCurrent, refreshAccess } = useToolAccess();
    const canManage = canEditCurrent;
    const grantableToolKeys = visibleToolKeys(GRANTABLE_TOOL_KEYS, isSuperAdmin);
    const [members, setMembers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [accessRequests, setAccessRequests] = useState([]);
    const [projects, setProjects] = useState([]);
    const [projectGrants, setProjectGrants] = useState([]);
    const [approving, setApproving] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteMessage, setInviteMessage] = useState('');
    const [selectedNewOwner, setSelectedNewOwner] = useState('');
    const [ownershipSaving, setOwnershipSaving] = useState(false);
    const [ownershipMessage, setOwnershipMessage] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState('');
    const [orgName, setOrgName] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [logoSaving, setLogoSaving] = useState(false);
    const [logoMessage, setLogoMessage] = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const [membersRes, meRes] = await Promise.all([
                apiFetch('/api/auth/organization/users'),
                apiFetch('/api/auth/me')
            ]);
            if (!membersRes.ok) {
                const body = await membersRes.json().catch(() => ({}));
                throw new Error(body.message || 'Unable to load members.');
            }
            setMembers(await membersRes.json());
            if (meRes.ok) {
                const me = await meRes.json();
                setOrgName(me.company || me.organizationId || '');
            }
            if (canManage) {
                const [inviteRes, requestRes, projectRes, grantRes] = await Promise.all([
                    apiFetch('/api/auth/organization/invitations'),
                    apiFetch('/api/auth/organization/access-requests'),
                    apiFetch('/api/projects'),
                    apiFetch('/api/projects/access')
                ]);
                if (inviteRes.ok) setInvitations(await inviteRes.json());
                if (requestRes.ok) setAccessRequests(await requestRes.json());
                if (projectRes.ok) setProjects(await projectRes.json());
                if (grantRes.ok) setProjectGrants(await grantRes.json());
            }
        } catch (err) {
            setError(err.message || 'Unable to load organization.');
        } finally {
            setLoading(false);
        }
    };

    const loadLogo = async () => {
        const res = await apiFetch('/api/auth/organization/logo');
        if (!res.ok) {
            setLogoUrl('');
            return;
        }
        const blob = await res.blob();
        setLogoUrl(current => {
            if (current) URL.revokeObjectURL(current);
            return URL.createObjectURL(blob);
        });
    };

    useEffect(() => {
        load();
    }, [canManage]);

    useEffect(() => {
        if (tab !== 'settings') return undefined;
        loadLogo();
        return () => {
            setLogoUrl(current => {
                if (current) URL.revokeObjectURL(current);
                return '';
            });
        };
    }, [tab]);

    const pendingInvites = useMemo(
        () => (invitations || []).filter(invite => !invite.isAccepted),
        [invitations]
    );

    const accessFor = (member, key) =>
        member.tools?.find(tool => tool.key === key)?.access || '';

    const updateMember = async (userId, payload) => {
        const res = await apiFetch(`/api/auth/organization/users/${userId}/tools`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.message || 'Unable to update access.');
        }
        await load();
        await refreshAccess?.();
    };

    const handleGrantChange = async (member, toolKey, access) => {
        try {
            await updateMember(member.id, {
                grants: [{ key: toolKey, access: access || null }]
            });
        } catch (err) {
            setError(err.message);
        }
    };

    const handlePrimaryChange = async (member, primaryToolKey) => {
        try {
            await updateMember(member.id, { primaryToolKey });
        } catch (err) {
            setError(err.message);
        }
    };

    const memberHasProject = (member, project) => {
        const email = (member.email || '').toLowerCase();
        if ((project.createdBy || '').toLowerCase() === email) return true;
        return projectGrants.some(grant =>
            grant.projectId === project.id && (grant.userEmail || '').toLowerCase() === email);
    };

    const handleProjectAccess = async (member, project, granted) => {
        const email = (member.email || '').toLowerCase();
        if ((project.createdBy || '').toLowerCase() === email) return;
        const res = await apiFetch(`/api/projects/${project.id}/access`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, granted })
        });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            setError(body.message || body || 'Unable to update project access.');
            return;
        }
        setProjectGrants(prev => {
            const without = prev.filter(grant =>
                !(grant.projectId === project.id && (grant.userEmail || '').toLowerCase() === email));
            return granted ? [...without, { projectId: project.id, userEmail: email }] : without;
        });
    };

    const handleRejectRequest = async (requestId) => {
        const res = await apiFetch(`/api/auth/organization/access-requests/${requestId}/reject`, { method: 'POST' });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            setError(body.message || 'Unable to reject the request.');
            return;
        }
        setInviteMessage('Access request rejected.');
        await load();
    };

    const handleApproved = async () => {
        setApproving(null);
        setInviteMessage('Access request approved.');
        await load();
    };

    const handleRemove = async (member) => {
        if (!window.confirm(`Remove ${member.fullName || member.email} from this organization?`)) return;
        const res = await apiFetch(`/api/auth/organization/users/${member.id}`, { method: 'DELETE' });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            setError(body.message || 'Unable to remove member.');
            return;
        }
        await load();
    };

    const handleInvited = async ({ message }) => {
        setInviteMessage(message || 'Invitation sent.');
        await load();
    };

    const handleRevoke = async (invitationId) => {
        const res = await apiFetch(`/api/auth/organization/invitations/${invitationId}`, { method: 'DELETE' });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            setError(body.message || 'Unable to revoke invitation.');
            return;
        }
        await load();
    };

    const handleResend = async (invitationId) => {
        const res = await apiFetch(`/api/auth/organization/invitations/${invitationId}/resend`, { method: 'POST' });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            setError(body.message || 'Unable to resend invitation.');
            return;
        }
        setInviteMessage('Invitation resent.');
        await load();
    };

    const handleTransfer = async (e) => {
        e.preventDefault();
        setOwnershipSaving(true);
        setOwnershipMessage('');
        try {
            const res = await apiFetch('/api/auth/organization/transfer-ownership', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newRootAdminUserId: selectedNewOwner })
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body.message || 'Transfer failed.');
            setOwnershipMessage('Ownership transferred.');
            await load();
            await refreshAccess?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setOwnershipSaving(false);
        }
    };

    const notifyLogoChanged = () => {
        window.dispatchEvent(new Event('organization-logo-changed'));
    };

    const handleLogoUpload = async (e) => {
        e.preventDefault();
        if (!logoFile) return;
        setLogoSaving(true);
        setLogoMessage('');
        setError('');
        try {
            const body = new FormData();
            body.append('file', logoFile);
            const res = await apiFetch('/api/auth/organization/logo', { method: 'POST', body });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(payload.message || 'Unable to upload the logo.');
            setLogoFile(null);
            setLogoMessage('Logo updated.');
            await loadLogo();
            notifyLogoChanged();
        } catch (err) {
            setError(err.message);
        } finally {
            setLogoSaving(false);
        }
    };

    const handleLogoRemove = async () => {
        setLogoSaving(true);
        setLogoMessage('');
        setError('');
        try {
            const res = await apiFetch('/api/auth/organization/logo', { method: 'DELETE' });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(payload.message || 'Unable to remove the logo.');
            if (logoUrl) URL.revokeObjectURL(logoUrl);
            setLogoUrl('');
            setLogoFile(null);
            setLogoMessage('Logo removed.');
            notifyLogoChanged();
        } catch (err) {
            setError(err.message);
        } finally {
            setLogoSaving(false);
        }
    };

    const handleDeleteOrg = async (e) => {
        e.preventDefault();
        if (!window.confirm('This permanently deletes the organization. Continue?')) return;
        setOwnershipSaving(true);
        try {
            const res = await apiFetch('/api/auth/organization', { method: 'DELETE' });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.message || 'Delete failed.');
            }
            sessionStorage.removeItem('token');
            window.location.href = '/';
        } catch (err) {
            setError(err.message);
            setOwnershipSaving(false);
        }
    };

    const title = tab === 'invitations' ? 'Invitations' : tab === 'settings' ? 'Organization settings' : 'Members';
    const showInviteButton = canManage && (tab === 'members' || tab === 'invitations');

    return (
        <div style={{ padding: '1rem 1.25rem', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                <div>
                    <h1 style={{ fontSize: '1.35rem', margin: 0 }}>{title}</h1>
                    <p style={{ color: 'var(--text-secondary)', marginTop: '0.35rem' }}>
                        Invite people, set per-tool View or Edit access, and keep the organization owner in place.
                    </p>
                </div>
                {showInviteButton && (
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => setInviteOpen(true)}
                        style={{ flexShrink: 0 }}
                    >
                        Invite member
                    </button>
                )}
            </div>

            {error && (
                <div style={{ marginTop: '1rem', color: 'var(--error)' }}>{error}</div>
            )}
            {inviteMessage && (
                <div style={{ marginTop: '1rem', color: 'var(--success)' }}>{inviteMessage}</div>
            )}

            {loading ? (
                <div style={{ marginTop: '2rem' }}>Loading…</div>
            ) : tab === 'members' ? (
                <>
                    {canManage && accessRequests.length > 0 && (
                        <div style={{ marginTop: '0.75rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                            <div style={{ padding: '0.75rem 1rem', background: 'var(--bg-secondary)', fontWeight: 600 }}>
                                Access requests
                                <span className="sidebar-nav-badge" style={{ marginLeft: '0.5rem' }}>{accessRequests.length}</span>
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <tbody>
                                    {accessRequests.map(request => (
                                        <tr key={request.id} style={{ borderTop: '1px solid var(--border)' }}>
                                            <td style={{ padding: '0.75rem' }}>
                                                <div style={{ fontWeight: 600 }}>{request.fullName || request.email}</div>
                                                <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{request.email}</div>
                                            </td>
                                            <td style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}>Request for access pending</td>
                                            <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                                                <button className="btn-primary" style={{ marginRight: '0.5rem' }} onClick={() => setApproving(request)}>Approve</button>
                                                <button className="btn-secondary" onClick={() => handleRejectRequest(request.id)}>Reject</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div style={{ marginTop: '0.75rem', overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                            <thead>
                                <tr style={{ background: 'var(--bg-secondary)' }}>
                                    <th style={{ textAlign: 'left', padding: '0.75rem' }}>Member</th>
                                    <th style={{ textAlign: 'left', padding: '0.75rem' }}>Primary</th>
                                    {grantableToolKeys.map(key => (
                                        <th key={key} style={{ textAlign: 'left', padding: '0.75rem' }}>{tools[key].label}</th>
                                    ))}
                                    <th style={{ textAlign: 'left', padding: '0.75rem' }}>Projects</th>
                                    {canManage && <th style={{ padding: '0.75rem' }} />}
                                </tr>
                            </thead>
                            <tbody>
                                {members.map(member => (
                                    <tr key={member.id} style={{ borderTop: '1px solid var(--border)' }}>
                                        <td style={{ padding: '0.75rem' }}>
                                            <div style={{ fontWeight: 600 }}>{member.fullName}</div>
                                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{member.email}</div>
                                            {member.isRootAdmin && <span style={{ fontSize: '0.75rem', color: '#10B981' }}>Owner</span>}
                                            {member.isSuperAdmin && (
                                                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginLeft: member.isRootAdmin ? '0.5rem' : 0 }}>
                                                    Platform admin
                                                </span>
                                            )}
                                        </td>
                                        {member.isSuperAdmin ? (
                                            <>
                                                <td style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}>—</td>
                                                {grantableToolKeys.map(key => (
                                                    <td
                                                        key={key}
                                                        style={{ padding: '0.75rem', color: 'var(--text-secondary)' }}
                                                        title="Platform admins keep full access to every tool. Organization None/View/Edit does not apply."
                                                    >
                                                        Full access
                                                    </td>
                                                ))}
                                            </>
                                        ) : (
                                            <>
                                                <td style={{ padding: '0.75rem' }}>
                                                    {canManage ? (
                                                        <select className="input-field" value={member.primaryToolKey || 'Logic'} onChange={e => handlePrimaryChange(member, e.target.value)}>
                                                            {grantableToolKeys.map(key => <option key={key} value={key}>{tools[key].label}</option>)}
                                                        </select>
                                                    ) : (tools[member.primaryToolKey]?.label || member.primaryToolKey)}
                                                </td>
                                                {grantableToolKeys.map(key => {
                                                    const primaryKey = member.primaryToolKey || 'Logic';
                                                    const options = ACCESS_OPTIONS.filter(opt => opt.value !== '' || key !== primaryKey);
                                                    return (
                                                        <td key={key} style={{ padding: '0.75rem' }}>
                                                            {canManage ? (
                                                                <select
                                                                    className="input-field"
                                                                    value={accessFor(member, key)}
                                                                    disabled={member.isRootAdmin && key === 'Organization'}
                                                                    title={key === primaryKey ? 'The primary tool cannot be set to None. Assign a different primary tool first.' : undefined}
                                                                    onChange={e => handleGrantChange(member, key, e.target.value)}
                                                                >
                                                                    {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                                </select>
                                                            ) : (accessFor(member, key) || '—')}
                                                        </td>
                                                    );
                                                })}
                                            </>
                                        )}
                                        <td style={{ padding: '0.75rem' }}>
                                            {member.isRootAdmin || member.isSuperAdmin || accessFor(member, 'Organization') === 'Edit' ? (
                                                <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>All projects</span>
                                            ) : projects.length === 0 ? (
                                                <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>No projects</span>
                                            ) : (
                                                <ProjectAccessSelect
                                                    projects={projects}
                                                    selectedIds={projects.filter(project => memberHasProject(member, project)).map(project => project.id)}
                                                    lockedIds={projects
                                                        .filter(project => (project.createdBy || '').toLowerCase() === (member.email || '').toLowerCase())
                                                        .map(project => project.id)}
                                                    disabled={!canManage}
                                                    onToggle={(project, granted) => handleProjectAccess(member, project, granted)}
                                                />
                                            )}
                                        </td>
                                        {canManage && (
                                            <td style={{ padding: '0.75rem' }}>
                                                <button
                                                    className="btn-secondary"
                                                    disabled={member.isRootAdmin}
                                                    title={member.isRootAdmin ? 'The organization owner cannot be removed' : 'Remove member'}
                                                    onClick={() => handleRemove(member)}
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : tab === 'invitations' ? (
                <div style={{ marginTop: '0.75rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                    {!canManage ? (
                        <div style={{ padding: '1.5rem', color: 'var(--text-secondary)' }}>You can view members, but only Organization editors can manage invitations.</div>
                    ) : pendingInvites.length === 0 ? (
                        <div style={{ padding: '1.5rem', color: 'var(--text-secondary)' }}>No pending invitations.</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                            <thead>
                                <tr style={{ background: 'var(--bg-secondary)' }}>
                                    <th style={{ textAlign: 'left', padding: '0.75rem' }}>Email</th>
                                    <th style={{ textAlign: 'left', padding: '0.75rem' }}>Primary</th>
                                    <th style={{ textAlign: 'left', padding: '0.75rem' }}>Sent</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {pendingInvites.map(invite => (
                                    <tr key={invite.id} style={{ borderTop: '1px solid var(--border)' }}>
                                        <td style={{ padding: '0.75rem' }}>{invite.email}</td>
                                        <td style={{ padding: '0.75rem' }}>{tools[invite.primaryToolKey]?.label || invite.primaryToolKey}</td>
                                        <td style={{ padding: '0.75rem' }}>{formatInviteSentDate(invite.createdAt)}</td>
                                        <td style={{ padding: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                                            <button className="btn-secondary" onClick={() => handleResend(invite.id)}>Resend</button>
                                            <button className="btn-secondary" onClick={() => handleRevoke(invite.id)}>Revoke</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            ) : (
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div style={{ padding: '1.25rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <h3 style={{ margin: 0 }}>Logo</h3>
                        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                            Shown on the left of the header. PNG, JPEG, or WebP, up to 1 MB.
                        </p>
                        {logoUrl && (
                            <img src={logoUrl} alt={orgName || 'Organization logo'} style={{ height: '48px', width: 'auto', maxWidth: '240px', objectFit: 'contain', alignSelf: 'flex-start' }} />
                        )}
                        {logoMessage && <div style={{ color: 'var(--success)' }}>{logoMessage}</div>}
                        {canManage ? (
                            <form onSubmit={handleLogoUpload} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                <input
                                    className="input-field"
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    onChange={e => setLogoFile(e.target.files?.[0] || null)}
                                />
                                <button className="btn-primary" disabled={logoSaving || !logoFile}>Upload</button>
                                {logoUrl && (
                                    <button type="button" className="btn-secondary" disabled={logoSaving} onClick={handleLogoRemove}>Remove</button>
                                )}
                            </form>
                        ) : !logoUrl && (
                            <p style={{ margin: 0, color: 'var(--text-secondary)' }}>This organization has no logo yet.</p>
                        )}
                    </div>
                    {!isRootAdmin ? (
                        <p style={{ color: 'var(--text-secondary)' }}>Only the organization owner can transfer ownership or delete the organization.</p>
                    ) : (
                        <>
                            {ownershipMessage && <div style={{ color: 'var(--success)' }}>{ownershipMessage}</div>}
                            <form onSubmit={handleTransfer} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                <select className="input-field" required value={selectedNewOwner} onChange={e => setSelectedNewOwner(e.target.value)} style={{ flex: 1 }}>
                                    <option value="">Transfer ownership to…</option>
                                    {members.filter(m => !m.isRootAdmin && !m.isSuperAdmin).map(m => (
                                        <option key={m.id} value={m.id}>{m.fullName} ({m.email})</option>
                                    ))}
                                </select>
                                <button className="btn-secondary" disabled={ownershipSaving || !selectedNewOwner}>Transfer</button>
                            </form>
                            <form onSubmit={handleDeleteOrg} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '1.25rem', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius)' }}>
                                <h3 style={{ margin: 0, color: 'var(--error)' }}>Delete organization</h3>
                                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Type the organization name to confirm{orgName ? `: ${orgName}` : ''}.</p>
                                <input className="input-field" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} />
                                <button className="btn-primary" style={{ background: 'var(--error)', alignSelf: 'flex-start' }} disabled={ownershipSaving || (orgName && deleteConfirm !== orgName)}>Delete organization</button>
                            </form>
                        </>
                    )}
                </div>
            )}

            <InviteMemberModal
                isOpen={inviteOpen}
                onClose={() => setInviteOpen(false)}
                onInvited={handleInvited}
                projects={projects}
            />
            <ApproveAccessModal
                request={approving}
                onClose={() => setApproving(null)}
                onApproved={handleApproved}
            />
        </div>
    );
}
